import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Breadcrumbs,
  Button,
  CircularProgress,
  Link as MuiLink,
  Paper,
  Stack,
  Typography,
} from "@mui/material";
import { Link, useNavigate, useParams } from "react-router-dom";
import { Formik, Form } from "formik";
import { useTranslation } from "react-i18next";
import { useSnackbar } from "notistack";
import useJumboAuth from "@jumbo/hooks/useJumboAuth";
import { useJumboDialog } from "@jumbo/components/JumboDialog/hooks/useJumboDialog";
import {
  createPosWorkspacesDocument,
  getPosWorkspacesDocument,
  isPosWorkspacesDocumentPersisted,
  updatePosWorkspacesDocument,
} from "app/services/api/accessSystemPosWorkspaces";
import { getLoggedInCustomerId } from "app/pages/customers/dsok/access-system/accessSystemLoggedInCustomer";
import {
  findWorkspaceDataIndexByUuid,
  normalizeWorkspacesForUi,
} from "app/pages/customers/dsok/utils/normalizeDsokWorkspaces";
import { buildDsokWorkspacesApiPayloadFromCustomerWorkspaces } from "app/pages/customers/dsok/workspaces/buildDsokWorkspacesApiPayload";
import { mergeWorkspaceWithDefaults } from "app/pages/customers/dsok/workspaces/dsokWorkspaceFormConstants";
import DsokWorkspaceSingleWorkspaceForm from "app/pages/customers/dsok/workspaces/DsokWorkspaceSingleWorkspaceForm";

const LIST_PATH = "/customer/dsok/pos/workspaces";

const DsokPosWorkspaceDetailPage = () => {
  const { workspaceUuid: workspaceUuidParam } = useParams();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();
  const { showDialog, hideDialog } = useJumboDialog();
  const { getAuthUser } = useJumboAuth();
  const customerId = getLoggedInCustomerId(getAuthUser);

  const [saving, setSaving] = useState(false);

  const workspaceUuid = useMemo(
    () => String(workspaceUuidParam ?? "").trim(),
    [workspaceUuidParam],
  );

  const workspacesTitle = t("pages.pos.workspacesTitle");

  const [pageLoading, setPageLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [notFound, setNotFound] = useState(false);
  const [documentPersisted, setDocumentPersisted] = useState(false);
  const [formInitialValues, setFormInitialValues] = useState(null);
  const [formKey, setFormKey] = useState(0);
  const [lineOfBusiness, setLineOfBusiness] = useState(null);

  const [activeWorkspaceSection, setActiveWorkspaceSection] =
    useState("features");
  const [activeDeviceUuid, setActiveDeviceUuid] = useState(null);

  const applyLoadedDocument = useCallback((doc) => {
    const normalized = normalizeWorkspacesForUi(doc && typeof doc === "object" ? doc : {});
    const rawData = Array.isArray(normalized.data) ? normalized.data : [];
    const data = rawData.map((ws) => mergeWorkspaceWithDefaults(ws));
    setLineOfBusiness(
      doc && typeof doc === "object" && doc.lineOfBusiness != null
        ? String(doc.lineOfBusiness).trim()
        : null,
    );
    setDocumentPersisted(isPosWorkspacesDocumentPersisted(doc));
    setFormInitialValues({
      customer: {
        workspaces: {
          data,
        },
      },
    });
    setFormKey((k) => k + 1);
  }, []);

  const load = useCallback(async () => {
    setPageLoading(true);
    setLoadError(false);
    setNotFound(false);
    try {
      const doc = await getPosWorkspacesDocument();
      applyLoadedDocument(doc && typeof doc === "object" ? doc : {});
      setLoadError(false);
    } catch (e) {
      console.error(e);
      setLoadError(true);
      setFormInitialValues(null);
      setDocumentPersisted(false);
    } finally {
      setPageLoading(false);
    }
  }, [applyLoadedDocument]);

  useEffect(() => {
    void load();
  }, [load]);

  const wsIndexInitial = useMemo(() => {
    if (!formInitialValues?.customer?.workspaces?.data || !workspaceUuid) {
      return -1;
    }
    return findWorkspaceDataIndexByUuid(
      formInitialValues.customer.workspaces.data,
      workspaceUuid,
    );
  }, [formInitialValues, workspaceUuid]);

  useEffect(() => {
    if (pageLoading || loadError || !formInitialValues) return;
    if (!workspaceUuid) {
      setNotFound(true);
      return;
    }
    if (wsIndexInitial < 0) {
      setNotFound(true);
    }
  }, [pageLoading, loadError, formInitialValues, workspaceUuid, wsIndexInitial]);

  useEffect(() => {
    setActiveWorkspaceSection("features");
    setActiveDeviceUuid(null);
  }, [workspaceUuid]);

  if (pageLoading && !formInitialValues) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight={240}
        sx={{ width: "100%", py: 4 }}
      >
        <CircularProgress />
      </Box>
    );
  }

  if (loadError) {
    return (
      <Box sx={{ width: "100%", p: 2 }}>
        <Breadcrumbs aria-label="breadcrumb" sx={{ mb: 1 }}>
          <Link to="/">{t("sidebar.menuItem.home")}</Link>
          <MuiLink
            component={Link}
            to="/customer/dsok"
            color="inherit"
            underline="hover"
          >
            {t("pages.pos.breadcrumbCustomer")}
          </MuiLink>
          <Typography color="text.secondary">{t("sidebar.menu.pos")}</Typography>
          <MuiLink
            component={Link}
            to={LIST_PATH}
            color="inherit"
            underline="hover"
          >
            {workspacesTitle}
          </MuiLink>
        </Breadcrumbs>
        <Alert severity="error">{t("pages.pos.workspaces.loadError")}</Alert>
      </Box>
    );
  }

  if (notFound || wsIndexInitial < 0 || !formInitialValues) {
    return (
      <Box sx={{ width: "100%", p: 2 }}>
        <Breadcrumbs aria-label="breadcrumb" sx={{ mb: 1 }}>
          <Link to="/">{t("sidebar.menuItem.home")}</Link>
          <MuiLink
            component={Link}
            to="/customer/dsok"
            color="inherit"
            underline="hover"
          >
            {t("pages.pos.breadcrumbCustomer")}
          </MuiLink>
          <Typography color="text.secondary">{t("sidebar.menu.pos")}</Typography>
          <MuiLink
            component={Link}
            to={LIST_PATH}
            color="inherit"
            underline="hover"
          >
            {workspacesTitle}
          </MuiLink>
        </Breadcrumbs>
        <Alert severity="warning" sx={{ mb: 2 }}>
          {t("pages.pos.workspaces.detailNotFound", {
            defaultValue: "Pracovisko sa nepodarilo nájsť.",
          })}
        </Alert>
        <Button component={Link} to={LIST_PATH} variant="outlined">
          {t("buttons.back", { defaultValue: "Späť" })}
        </Button>
      </Box>
    );
  }

  return (
    <React.Fragment>
      <Formik
        key={`${formKey}-${workspaceUuid}`}
        initialValues={formInitialValues}
        enableReinitialize={false}
        onSubmit={() => {}}
      >
        {({ values, setErrors, setTouched }) => {
          const liveWsIndex = findWorkspaceDataIndexByUuid(
            values?.customer?.workspaces?.data,
            workspaceUuid,
          );

          const displayName =
            String(
              values?.customer?.workspaces?.data?.[liveWsIndex]?.name ?? "",
            ).trim() || workspaceUuid;

          const handleSave = async () => {
            if (liveWsIndex < 0) return;

            const wsRow = values?.customer?.workspaces?.data?.[liveWsIndex];
            const devicesArray = Array.isArray(wsRow?.devices) ? wsRow.devices : [];
            const deviceRowId = (d, i) =>
              d?.uuid != null && String(d.uuid).trim() !== ""
                ? String(d.uuid).trim()
                : `device-tab-${liveWsIndex}-${i}`;
            const onlyDeviceUuid =
              activeDeviceUuid != null && String(activeDeviceUuid).trim() !== ""
                ? String(activeDeviceUuid).trim()
                : devicesArray[0] != null
                  ? deviceRowId(devicesArray[0], 0)
                  : undefined;

            const payload = buildDsokWorkspacesApiPayloadFromCustomerWorkspaces(
              values?.customer?.workspaces ?? {},
              t,
              setErrors,
              setTouched,
              {
                onlyWorkspaceDataIndex: liveWsIndex,
                ...(onlyDeviceUuid != null ? { onlyDeviceUuid } : {}),
              },
            );
            if (!payload) {
              enqueueSnackbar(t("messages.checkRequiredFields"), {
                variant: "warning",
              });
              return;
            }

            if (customerId == null || String(customerId).trim() === "") {
              enqueueSnackbar(t("pages.pos.workspaces.loadMissingCustomer"), {
                variant: "error",
              });
              return;
            }

            setSaving(true);
            try {
              if (!documentPersisted) {
                await createPosWorkspacesDocument({
                  customerId,
                  data: payload.data,
                });
                setDocumentPersisted(true);
              }
              await updatePosWorkspacesDocument(customerId, {
                data: payload.data,
              });
              enqueueSnackbar(t("pages.pos.workspaces.saveSuccess"), {
                variant: "success",
              });
              const doc = await getPosWorkspacesDocument();
              applyLoadedDocument(doc && typeof doc === "object" ? doc : {});
            } catch (e) {
              const msg =
                e?.response?.data?.message ||
                e?.message ||
                t("pages.pos.workspaces.saveError", {
                  defaultValue: t("messages.error"),
                });
              enqueueSnackbar(msg, { variant: "error" });
            } finally {
              setSaving(false);
            }
          };

          const handleDeleteWorkspace = () => {
            if (liveWsIndex < 0) return;

            showDialog({
              variant: "confirm",
              title: t("dsokCustomerDetail.workspaces.deleteConfirm.title", {
                defaultValue: "Delete workspace?",
              }),
              onYes: async () => {
                hideDialog();
                const nextData = normalizeWorkspacesForUi(
                  values?.customer?.workspaces ?? {},
                ).data.filter((_, i) => i !== liveWsIndex);
                const nextSlice = {
                  ...values.customer.workspaces,
                  data: nextData,
                };

                setSaving(true);
                try {
                  const payload =
                    buildDsokWorkspacesApiPayloadFromCustomerWorkspaces(
                      nextSlice,
                      t,
                      setErrors,
                      setTouched,
                    );
                  if (!payload) {
                    setSaving(false);
                    return;
                  }

                  if (customerId == null || String(customerId).trim() === "") {
                    enqueueSnackbar(t("pages.pos.workspaces.loadMissingCustomer"), {
                      variant: "error",
                    });
                    setSaving(false);
                    return;
                  }

                  let persisted = documentPersisted;
                  if (!persisted) {
                    await createPosWorkspacesDocument({
                      customerId,
                      data: payload.data,
                    });
                    persisted = true;
                    setDocumentPersisted(true);
                  }
                  await updatePosWorkspacesDocument(customerId, {
                    data: payload.data,
                  });
                  enqueueSnackbar(t("pages.pos.workspaces.deleteItemSuccess"), {
                    variant: "success",
                  });
                  navigate(LIST_PATH);
                } catch (e) {
                  const msg =
                    e?.response?.data?.message ||
                    e?.message ||
                    t("pages.pos.workspaces.deleteItemError");
                  enqueueSnackbar(msg, { variant: "error" });
                } finally {
                  setSaving(false);
                }
              },
              onNo: hideDialog,
            });
          };

          if (liveWsIndex < 0) {
            return (
              <Box sx={{ p: 2 }}>
                <Alert severity="warning">
                  {t("pages.pos.workspaces.detailNotFound", {
                    defaultValue: "Pracovisko sa nepodarilo nájsť v dátach.",
                  })}
                </Alert>
                <Button
                  component={Link}
                  to={LIST_PATH}
                  variant="outlined"
                  sx={{ mt: 2 }}
                >
                  {t("buttons.back", { defaultValue: "Späť" })}
                </Button>
              </Box>
            );
          }

          return (
            <Form>
              <Breadcrumbs aria-label="breadcrumb" sx={{ mb: 1 }}>
                <Link to="/">{t("sidebar.menuItem.home")}</Link>
                <MuiLink
                  component={Link}
                  to="/customer/dsok"
                  color="inherit"
                  underline="hover"
                >
                  {t("pages.pos.breadcrumbCustomer")}
                </MuiLink>
                <Typography color="text.secondary">
                  {t("sidebar.menu.pos")}
                </Typography>
                <MuiLink
                  component={Link}
                  to={LIST_PATH}
                  color="inherit"
                  underline="hover"
                >
                  {workspacesTitle}
                </MuiLink>
                <Typography color="text.primary">{displayName}</Typography>
              </Breadcrumbs>

              <Stack
                direction="row"
                alignItems="center"
                justifyContent="space-between"
                spacing={2}
                mb={2}
                flexWrap="wrap"
              >
                <Typography variant="h2">{displayName}</Typography>
                <Stack direction="row" spacing={1}>
                  <Button
                    component={Link}
                    to={LIST_PATH}
                    variant="outlined"
                    disabled={saving}
                  >
                    {t("buttons.cancel", { defaultValue: "Zrušiť" })}
                  </Button>
                  <Button
                    type="button"
                    variant="contained"
                    disabled={saving}
                    onClick={() => void handleSave()}
                  >
                    {saving ? (
                      <CircularProgress size={22} color="inherit" />
                    ) : (
                      t("buttons.save")
                    )}
                  </Button>
                </Stack>
              </Stack>

              <Paper
                variant="outlined"
                sx={{
                  bgcolor: "background.paper",
                  borderRadius: 1,
                  overflow: "hidden",
                }}
              >
                <DsokWorkspaceSingleWorkspaceForm
                  wsIndex={liveWsIndex}
                  workspaceUuid={workspaceUuid}
                  customerIdOverride={customerId}
                  activeWorkspaceSection={activeWorkspaceSection}
                  setActiveWorkspaceSection={setActiveWorkspaceSection}
                  activeDeviceUuid={activeDeviceUuid}
                  setActiveDeviceUuid={setActiveDeviceUuid}
                  showDialog={showDialog}
                  hideDialog={hideDialog}
                  onDeleteWorkspace={handleDeleteWorkspace}
                  showDeleteWorkspaceButton={false}
                  showWorkspaceIsActiveToggle
                  allowCompressor={lineOfBusiness === "concreteStation"}
                />
              </Paper>
            </Form>
          );
        }}
      </Formik>
    </React.Fragment>
  );
};

export default DsokPosWorkspaceDetailPage;
